import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "../services/api";
import Table from "../components/common/Table";
import TableLoader from "../components/common/TableLoader";

const ContactQueriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "");
  const [sourceFilter, setSourceFilter] = useState(() => searchParams.get("source") || "");
  const [pagination, setPagination] = useState(() => {
    const pageParam = searchParams.get("page");
    const limitParam = searchParams.get("limit");
    return {
      page: pageParam ? parseInt(pageParam) : 1,
      limit: limitParam ? parseInt(limitParam) : 10,
      total: 0,
      totalPages: 0,
    };
  });
  const [stats, setStats] = useState({
    total: 0,
    NEW: 0,
    IN_PROGRESS: 0,
    CONTACTED: 0,
    RESOLVED: 0,
    CLOSED: 0,
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, sourceFilter]);

  const fetchContacts = async () => {
    if (isInitialLoading) {
      setIsInitialLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;

      const response = await adminApi.getContactQueries(params);

      if (response.data.success) {
        setContacts(response.data.data);
        setPagination((prev) => ({
          ...prev,
          ...response.data.pagination,
        }));
      }
      setError("");
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError(err.response?.data?.error || "Failed to fetch contact queries");
    } finally {
      setIsInitialLoading(false);
      setIsRefetching(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getContactStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const viewContact = async (contactId) => {
    try {
      const response = await adminApi.getContactById(contactId);
      if (response.data.success) {
        setSelectedContact(response.data.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error("Error viewing contact:", err);
      alert("Failed to load contact details");
    }
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
      setSearchTerm(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSourceFilterChange = (value) => {
    setSourceFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setStatusFilter("");
    setSourceFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (statusFilter) params.set("status", statusFilter);
    if (sourceFilter) params.set("source", sourceFilter);
    if (pagination.page !== 1) params.set("page", pagination.page);
    if (pagination.limit !== 10) params.set("limit", pagination.limit);
    setSearchParams(params);
  };

  useEffect(() => {
    updateURLParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, sourceFilter, pagination.page, pagination.limit]);

  const getStatusColor = (status) => {
    const colors = {
      NEW: "bg-blue-100 text-blue-800",
      IN_PROGRESS: "bg-yellow-100 text-yellow-800",
      CONTACTED: "bg-purple-100 text-purple-800",
      RESOLVED: "bg-green-100 text-green-800",
      CLOSED: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getSourceColor = (source) => {
    return source === "public" ? "bg-indigo-100 text-indigo-800" : "bg-pink-100 text-pink-800";
  };

  const handlePageChange = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (contact) => (
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[10px] shrink-0 bg-indigo"
          >
            {contact.name.charAt(0).toUpperCase()}
          </div>
          <span className="text-xs font-medium">{contact.name}</span>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (contact) => <span className="text-xs text-muted">{contact.email}</span>,
    },
    {
      key: "subject",
      label: "Subject",
      render: (contact) => (
        <span className="text-xs text-ink truncate max-w-[200px] inline-block" title={contact.subject}>
          {contact.subject}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (contact) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(contact.status)}`}>
          {contact.status.replace("_", " ")}
        </span>
      ),
    },
    {
      key: "source",
      label: "Source",
      render: (contact) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${getSourceColor(contact.source)}`}>
          {contact.source}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Date",
      render: (contact) => <span className="font-mono text-[11px] text-muted whitespace-nowrap">{formatDate(contact.createdAt)}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      render: (contact) => (
        <button
          onClick={() => viewContact(contact._id)}
          className="py-1.5 px-3 border border-line rounded-lg bg-white text-[13px] font-medium hover:bg-soft transition-colors"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-8">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Contact Us Queries</h1>
        <p className="text-muted text-[15px] mt-2 mb-0">
          View and manage contact form submissions from users across public and private applications.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-6 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.total}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">New</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-blue-700">{stats.NEW}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">In Progress</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-yellow-700">{stats.IN_PROGRESS}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Contacted</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-purple-700">{stats.CONTACTED}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Resolved</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">{stats.RESOLVED}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Closed</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-gray-700">{stats.CLOSED}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5">
        <div className="flex gap-3 mb-3 items-center flex-wrap">
          <div className="relative w-[320px]">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name, email, subject..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full py-2.5 px-3 pl-10 pr-10 border border-line rounded-lg text-[13.5px] bg-white transition-all focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10 placeholder:text-muted/50"
            />
            {searchInput && (
              <button
                onClick={handleSearchClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors p-1 rounded-md hover:bg-soft"
                title="Clear search"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* <div className="relative min-w-[160px]">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="w-full py-2.5 px-3.5 pr-10 border border-line rounded-lg bg-white cursor-pointer text-[13px] font-medium text-ink hover:bg-soft transition-all appearance-none focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            >
              <option value="">All Status</option>
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="CONTACTED">Contacted</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div> */}

          <div className="relative min-w-[160px]">
            <select
              value={sourceFilter}
              onChange={(e) => handleSourceFilterChange(e.target.value)}
              className="w-full py-2.5 px-3.5 pr-10 border border-line rounded-lg bg-white cursor-pointer text-[13px] font-medium text-ink hover:bg-soft transition-all appearance-none focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            >
              <option value="">All Sources</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          <button
            onClick={clearFilters}
            disabled={searchTerm === "" && statusFilter === "" && sourceFilter === ""}
            className="py-2.5 px-3.5 border border-line rounded-lg bg-[#1e293b] cursor-pointer text-[13px] font-medium text-white hover:text-white hover:bg-[#334155] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="relative">
        <Table
          columns={columns}
          data={contacts}
          loading={isInitialLoading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          maxHeight="calc(100vh - 400px)"
          emptyState={
            <tr>
              <td colSpan={7} className="py-20 px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mb-4"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <h3 className="text-lg font-semibold text-ink mb-2">No Contact Queries Found</h3>
                  <p className="text-sm text-muted max-w-md">
                    {searchTerm || statusFilter || sourceFilter
                      ? "No queries match your current filters. Try adjusting your search criteria."
                      : "No contact queries have been submitted yet. Queries will appear here once users submit the contact form."}
                  </p>
                </div>
              </td>
            </tr>
          }
        />
        {isRefetching && <TableLoader overlay={true} message="Loading..." />}
      </div>

      {/* Modal for viewing contact details */}
      {showModal && selectedContact && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Contact Query Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-soft flex items-center justify-center text-muted hover:text-ink transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Name</div>
                  <div className="text-sm font-medium">{selectedContact.name}</div>
                </div>
                <div>
                  <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Email</div>
                  <div className="text-sm font-medium">{selectedContact.email}</div>
                </div>
                {selectedContact.phone && (
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Phone</div>
                    <div className="text-sm font-medium">{selectedContact.phone}</div>
                  </div>
                )}
                {selectedContact.company && (
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Company</div>
                    <div className="text-sm font-medium">{selectedContact.company}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Status</div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(selectedContact.status)}`}>
                    {selectedContact.status.replace("_", " ")}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Source</div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${getSourceColor(selectedContact.source)}`}>
                    {selectedContact.source}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Subject</div>
                <div className="text-sm font-medium">{selectedContact.subject}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Message</div>
                <div className="text-sm text-ink bg-soft p-4 rounded-lg whitespace-pre-wrap">{selectedContact.message}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-line">
                <div>
                  <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">Submitted</div>
                  <div className="text-sm font-mono">{formatDate(selectedContact.createdAt)}</div>
                </div>
                {selectedContact.ipAddress && (
                  <div>
                    <div className="text-xs text-muted uppercase tracking-wider font-semibold mb-1">IP Address</div>
                    <div className="text-sm font-mono">{selectedContact.ipAddress}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactQueriesPage;
